import { useEffect } from 'react';
import posthog from 'posthog-js';
import { useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useOrganizationContext } from '../contexts/OrganizationContext';

const POSTHOG_KEY = import.meta.env.VITE_POSTHOG_KEY as string | undefined;
const POSTHOG_HOST =
  (import.meta.env.VITE_POSTHOG_HOST as string | undefined) ?? 'https://app.posthog.com';

// No fallback key here on purpose — this used to default to the original
// project's PostHog key, so any deployment that forgot to set its own
// VITE_POSTHOG_KEY (e.g. a new owner's environment) would silently send
// its users' identify() calls — real emails and names — into that
// original project. Analytics simply stay off until a key is configured.
if (POSTHOG_KEY) {
  posthog.init(POSTHOG_KEY, {
    api_host: POSTHOG_HOST,
    person_profiles: 'identified_only',
    capture_pageview: false,
    capture_pageleave: true,
    autocapture: true,
  });
}

export function PostHogProvider({ children }: { children: React.ReactNode }) {
  const location = useLocation();
  const { user, isAuthenticated } = useAuth();
  const { currentOrg } = useOrganizationContext();

  useEffect(() => {
    if (!POSTHOG_KEY) return;

    if (isAuthenticated && user) {
      posthog.identify(user.id, {
        email: user.email,
        name: user.name,
        organization_id: currentOrg?.id,
        organization_name: currentOrg?.name,
        plan: currentOrg?.plan,
      });

      if (currentOrg) {
        posthog.group('organization', currentOrg.id, {
          name: currentOrg.name,
          plan: currentOrg.plan,
        });
      }
    } else {
      posthog.reset();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated, user?.id, currentOrg?.id]);

  useEffect(() => {
    if (!POSTHOG_KEY) return;
    posthog.capture('$pageview', { path: location.pathname });
  }, [location.pathname]);

  return <>{children}</>;
}
